<template>
  <div id="addon-vessel-report-list" v-if="reports">
    <div class="vx-card p-6">
      <div class="flex flex-wrap items-center mb-base">
        <div class="flex-grow">
          <vs-dropdown vs-trigger-click class="cursor-pointer">
            <div
              class="p-4 border border-solid d-theme-border-grey-light rounded-full d-theme-dark-bg cursor-pointer flex items-center justify-between font-medium"
            >
              <span class="mr-2"
                >{{
                  currentPage * paginationPageSize - (paginationPageSize - 1)
                }}
                -
                {{
                  totalReport - currentPage * paginationPageSize > 0
                    ? currentPage * paginationPageSize
                    : totalReport
                }}
                / {{ totalReport }}</span
              >
              <feather-icon icon="ChevronDownIcon" svgClasses="h-4 w-4" />
            </div>
            <vs-dropdown-menu>
              <vs-dropdown-item @click="onChangePageSize(10)">
                <span>10</span>
              </vs-dropdown-item>
              <vs-dropdown-item @click="onChangePageSize(20)">
                <span>20</span>
              </vs-dropdown-item>
              <vs-dropdown-item @click="onChangePageSize(30)">
                <span>30</span>
              </vs-dropdown-item>
            </vs-dropdown-menu>
          </vs-dropdown>
        </div>

        <vs-button
          class="sm:mr-4 mr-0 sm:w-auto w-full sm:order-normal order-3 sm:mt-0 mt-4"
          icon-pack="feather"
          icon="icon-search"
          color="primary"
          type="border"
          @click="showDialogReportSearchCondition"
          >検索条件を変更する</vs-button
        >
      </div>
      <ag-grid-vue
        ref="agGridTable"
        :components="components"
        :gridOptions="gridOptions"
        class="ag-theme-material w-100 my-4 ag-grid-table"
        :columnDefs="columnDefs"
        :defaultColDef="defaultColDef"
        :rowData="reports"
        rowSelection="multiple"
        colResizeDefault="shift"
        :animateRows="true"
        :floatingFilter="false"
        :pagination="true"
        :paginationPageSize="paginationPageSize"
        :suppressPaginationPanel="true"
        :enableRtl="$vs.rtl"
        :suppressNoRowsOverlay="true"
        :suppressDragLeaveHidesColumns="true"
        @grid-ready="onGridReady"
        @rowClicked="rowClicked"
      >
      </ag-grid-vue>

      <vs-pagination :total="totalPages" :max="7" v-model="currentPage" />
    </div>
    <dialog-report-search-condition
      :searchCondition.sync="searchCondition"
      ref="dialogReportSearchCondition"
      @searchReports="onSearchReports"
    ></dialog-report-search-condition>
  </div>
</template>

<script>
import { AgGridVue } from "ag-grid-vue";
import "@/assets/scss/vuexy/extraComponents/agGridStyleOverride.scss";
import DialogReportSearchCondition from "./components/DialogReportSearchCondition";
import CellRendererDate from "./cell-renderer/CellRendererDate";
import CellRendererConfirmerCount from "./cell-renderer/CellRendererConfirmerCount";

import { DateUtil } from "@/utils";

import { createNamespacedHelpers } from "vuex";
const { mapActions, mapState } = createNamespacedHelpers("vesselReportList");

const INITIAL_PAGE_SIZE = 10;

export default {
  components: {
    AgGridVue,
    DialogReportSearchCondition,
    // eslint-disable-next-line vue/no-unused-components
    CellRendererDate,
    // eslint-disable-next-line vue/no-unused-components
    CellRendererConfirmerCount,
  },
  data() {
    return {
      currentPage: 1,
      vesselData: null,
      searchCondition: {
        isLatest: "latest",
        fromDate: "",
        toDate: "",
      },
      searchQuery: "",
      searchName: "",
      // AgGrid
      gridApi: null,
      gridOptions: {
        localeText: { noRowsToShow: "船舶レポートが見つかりませんでした" },
        context: {
          componentParent: this,
        },
      },
      defaultColDef: {
        sortable: false,
        resizable: true,
        suppressMenu: true,
      },
      columnDefs: null,
      components: {
        CellRendererDate,
      },
      paginationPageSize: INITIAL_PAGE_SIZE, // 一覧に表示するレコードの数
    };
  },
  watch: {
    currentPage() {
      this.searchReports();
    },
  },
  computed: {
    ...mapState({
      reports: state => state.reports,
      pageable: state => state.pageable,
    }),

    totalPages() {
      if (this.pageable) return this.pageable.total_pages;
      else return 0;
    },

    totalReport() {
      if (this.pageable) return this.pageable.total_elements;
      else return 0;
    },
  },
  methods: {
    ...mapActions(["fetchReports", "resetReportListState"]),

    async onGridReady() {
      await this.searchReports();
      this.autoSizeAll();
    },

    onShowOverlayNoRows() {
      this.gridOptions.suppressNoRowsOverlay = false;
      this.gridApi.showNoRowsOverlay();
    },

    onOverlayHide() {
      this.gridApi.hideOverlay();
    },

    getCurrentPage() {
      return this.currentPage - 1;
    },

    rowClicked(evt) {
      this.$router.push({
        name: "addon-vessel-report-view",
        query: {
          id: evt.data.id,
          reportId: evt.data.report_id,
          vesselId: evt.data.vessel.id,
        },
      });
    },

    onChangePageSize(size) {
      this.paginationPageSize = size;
      this.currentPage = 1;
      this.searchReports();
    },

    onSearchReports() {
      this.currentPage = 1;
      this.searchReports();
    },

    showDialogReportSearchCondition() {
      this.$refs.dialogReportSearchCondition.show();
    },

    async searchReports() {
      this.onOverlayHide();
      this.$vs.loading();
      let payload = {
        is_current: this.isLatest(),
        page: this.getCurrentPage(),
        limit: this.paginationPageSize,
        range_date: this.rangeDate(),
      };
      try {
        await this.fetchReports(payload);
        this.$vs.loading.close();
        if (this.reports.length === 0) {
          this.onShowOverlayNoRows();
        }
      } catch (e) {
        this.$vs.loading.close();
        this.onShowOverlayNoRows();
      }
    },

    isLatest() {
      return this.searchCondition.isLatest === "latest";
    },

    rangeDate() {
      if (
        this.searchCondition.fromDate !== "" &&
        this.searchCondition.toDate !== ""
      ) {
        let fromDate = DateUtil.toYMD(
          this.searchCondition.fromDate,
          "YYYY/MM/DD"
        );
        let toDate = DateUtil.toYMD(this.searchCondition.toDate, "YYYY/MM/DD");
        return `${fromDate}-${toDate}`;
      }
      return null;
    },

    autoSizeAll() {
      let allColumnIds = [];
      this.gridColumnApi.getAllColumns().forEach(function(column) {
        allColumnIds.push(column.colId);
      });
      this.gridColumnApi.autoSizeColumns(allColumnIds, false);
    },

    cleanup() {
      this.resetReportListState();
    },
  },
  beforeMount() {
    this.columnDefs = [
      {
        headerName: "ID",
        field: "report_id",
        width: 200,
      },
      {
        headerName: "船名",
        field: "vessel.name_ja",
        width: 220,
      },
      {
        headerName: "閲覧人数",
        field: "enterprise_vessel_report_confirmers",
        cellRenderer: "CellRendererConfirmerCount",
        width: 150,
      },
      {
        headerName: "本船現在地",
        field: "current_position_name",
        width: 200,
      },
      {
        headerName: "連絡時間",
        field: "report_at",
        cellRenderer: "CellRendererDate",
        width: 250,
      },
      {
        headerName: "到着予定地",
        field: "estimated_arrival_port",
        width: 200,
      },
      {
        headerName: "到着予定日時",
        field: "estimated_arrival_at",
        cellRenderer: "CellRendererDate",
        width: 250,
      },
    ];
  },
  mounted() {
    let routerViewElement = document.getElementsByClassName("router-view");
    routerViewElement[0].style.padding = "2.2rem";

    this.gridApi = this.gridOptions.api;
    this.gridColumnApi = this.gridOptions.columnApi;
    if (this.$vs.rtl) {
      const header = this.$refs.agGridTable.$el.querySelector(
        ".ag-header-container"
      );
      header.style.left =
        "-" + String(Number(header.style.transform.slice(11, -3)) + 9) + "px";
    }
  },
  beforeDestroy() {
    this.cleanup();
  },
};
</script>

<style lang="scss" scoped>
#addon-vessel-report-list {
  .vx-input-group .prepend-text.btn-addon .vs-button {
    padding-top: -10px !important;
  }

  .vs-input-primary {
    width: 400px;
  }
  .vessel-list-filters {
    .vs__actions {
      position: absolute;
      right: 0;
      top: 50%;
      transform: translateY(-58%);
    }
  }
  .ag-grid-cell-chip {
    border-radius: 5px;
    &.vs-chip-success {
      background: rgba(var(--vs-success), 0.15);
      color: rgba(var(--vs-success), 1) !important;
      font-weight: 500;
    }
    &.vs-chip-warning {
      background: rgba(var(--vs-warning), 0.15);
      color: rgba(var(--vs-warning), 1) !important;
      font-weight: 500;
    }
    &.vs-chip-danger {
      background: rgba(var(--vs-danger), 0.15);
      color: rgba(var(--vs-danger), 1) !important;
      font-weight: 500;
    }
  }
}
</style>
